import { toast } from "@win-reality/react-ui";
import { useEffect } from "react";

import { HeatMapDataCard } from "@/components/players/HeatMapChart/DataCard";
import { ScatterChartDataCard } from "@/components/players/ScatterChartCard/ScatterChartDataCard";
import { SprayChartDataCard } from "@/components/players/SprayChartCard/SprayChartDataCard";
import { TiltAndSpinChartDataCard } from "@/components/players/TiltAndSpinChartCard";
import { Spinner } from "@/components/ui/spinner";
import { usePlayerProfileStore } from "@/stores/player-profile";

import { useLatestGame } from "../../Home/hooks/useLatestGame";
import { calculateLastThirtyDaysDateRange } from "../../utils/calculateDateRange";
import { useAvailablePitchTypes } from "./hooks/useAvailablePitchTypes";

interface PitcherChartsProps {
  playerExternalId: string;
}

export const PitcherCharts = ({ playerExternalId }: PitcherChartsProps) => {
  const dateFrom = usePlayerProfileStore((state) => state.dateFrom);
  const dateTo = usePlayerProfileStore((state) => state.dateTo);
  const setDateFrom = usePlayerProfileStore((state) => state.setDateFrom);
  const setDateTo = usePlayerProfileStore((state) => state.setDateTo);
  const gameType = usePlayerProfileStore((state) => state.gameType);
  const vsHand = usePlayerProfileStore((state) => state.vsHand);

  const {
    data: availablePitchTypes,
    isLoading: isLoadingAvailablePitchTypes,
    error: availablePitchTypesError,
  } = useAvailablePitchTypes({
    playerExternalId,
    startDate: dateFrom || "",
    endDate: dateTo || "",
    gameType,
    vsHandedness: vsHand,
    playerType: "pitcher",
  });

  const {
    data: latestGameData,
    isLoading: isLoadingLatestGameDefaultDate,
    error: latestGameDefaultDateError,
  } = useLatestGame({
    playerId: playerExternalId,
    playerType: "pitcher",
    showNoDataMessage: false,
  });

  const dateRange = calculateLastThirtyDaysDateRange(
    latestGameData?.latestGame?.game_started_at ?? null,
    latestGameData?.latestPractice?.game_started_at ?? null,
  );

  const hasSelectedDates = !!dateFrom && !!dateTo;

  useEffect(() => {
    // If the user has not selected dates, set the date range to the last 30 days
    if (!hasSelectedDates && dateRange) {
      setDateFrom(dateRange.startDate);
      setDateTo(dateRange.endDate);
    }
  }, [hasSelectedDates, dateRange, setDateFrom, setDateTo]);

  const hasError = !!latestGameDefaultDateError || !!availablePitchTypesError;

  useEffect(() => {
    if (hasError) {
      toast(
        "Something went wrong while loading the pitcher charts. Please try again.",
        { variant: "error" },
      );
    }
  }, [hasError]);

  if (isLoadingAvailablePitchTypes || isLoadingLatestGameDefaultDate) {
    return (
      <div className="flex flex-col h-full items-center justify-center gap-2 overflow-hidden p-2 mb-16">
        <Spinner className="h-12 w-12" />
        <p className="text-sm text-muted-foreground">
          Loading pitcher charts...
        </p>
      </div>
    );
  }

  if (!hasSelectedDates) {
    return (
      <div className="flex flex-col h-full items-center justify-center gap-2 overflow-hidden p-2 mb-16">
        <p className="text-sm text-muted-foreground">
          Please select a period or date to visualize some data
        </p>
      </div>
    );
  }

  const hasFastball = availablePitchTypes?.includes("Fastball");
  const hasOffspeed = availablePitchTypes?.includes("Offspeed");
  const hasChangeup = availablePitchTypes?.includes("Changeup");
  const hasCurveball = availablePitchTypes?.includes("Curveball");
  const hasSlider = availablePitchTypes?.includes("Slider");
  const hasCutter = availablePitchTypes?.includes("Cutter");
  const hasOther = availablePitchTypes?.includes("Other");

  return (
    <div className="flex flex-col h-full items-center gap-2 overflow-hidden p-2">
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 grid-rows-1 gap-2 w-full">
        <ScatterChartDataCard
          title="Break and Movement"
          playerExternalId={playerExternalId}
          startDate={dateFrom}
          endDate={dateTo}
          gameType={gameType}
          playerType="pitcher"
          vsHandedness={vsHand}
          chartType="break_and_movement"
          horizontalLabel="Horizontal Break"
          verticalLabel="Induced Vertical Break"
          unit="in"
          groupBy={["pitch_type"]}
        />

        <TiltAndSpinChartDataCard
          title="Tilt and Spin"
          playerExternalId={playerExternalId}
          startDate={dateFrom}
          endDate={dateTo}
          gameType={gameType}
          playerType="pitcher"
          vsHandedness={vsHand}
          chartType="tilt_and_spin"
          minSpin={1000}
          maxSpin={3000}
          rings={[1500, 2000, 2500, 3000]}
        />

        <ScatterChartDataCard
          title="Release Points"
          playerExternalId={playerExternalId}
          startDate={dateFrom}
          endDate={dateTo}
          gameType={gameType}
          playerType="pitcher"
          vsHandedness={vsHand}
          chartType="release_points"
          horizontalLabel="Horizontal Release"
          verticalLabel="Vertical Release"
          unit="ft"
          showOriginAxis={false}
          groupBy={["pitch_type"]}
        />
      </div>

      {gameType === "practice" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 w-full mx-auto">
          <HeatMapDataCard
            chartType="heatmap"
            title="All Pitches"
            playerExternalId={playerExternalId}
            startDate={dateFrom}
            endDate={dateTo}
            gameType={gameType}
            playerType="pitcher"
            vsHandedness={vsHand}
            groupBy={["pitch_type"]}
          />

          {hasFastball && (
            <HeatMapDataCard
              chartType="heatmap"
              title="Fastballs"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="pitcher"
              vsHandedness={vsHand}
              filters={{ is_fastball: "eq.true" }}
            />
          )}

          {hasOffspeed && (
            <HeatMapDataCard
              chartType="heatmap"
              title="Offspeed"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="pitcher"
              vsHandedness={vsHand}
              filters={{ is_offspeed: "eq.true" }}
            />
          )}
        </div>
      )}

      {gameType === "game" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 w-full mx-auto">
            {/* This is the row for this PR */}
            {dateFrom && dateTo && hasFastball && (
              <>
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Fastballs - Outs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  filters={{ is_fastball: "eq.true", is_out: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Fastballs - Hits"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  filters={{ is_fastball: "eq.true", is_hit: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Fastballs - Contact"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  filters={{ is_fastball: "eq.true", is_contact: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Fastballs - Whiffs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  filters={{ is_fastball: "eq.true", is_whiff: "eq.true" }}
                />
              </>
            )}
            {/* This is the row for this PR */}
            {dateFrom && dateTo && hasOffspeed && (
              <>
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Offspeed - Hits"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  filters={{ is_offspeed: "eq.true", is_hit: "eq.true" }}
                />

                <HeatMapDataCard
                  chartType="heatmap"
                  title="Offspeed - Contact"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  filters={{ is_offspeed: "eq.true", is_contact: "eq.true" }}
                />

                <HeatMapDataCard
                  chartType="heatmap"
                  title="Offspeed - Whiffs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  filters={{ is_offspeed: "eq.true", is_whiff: "eq.true" }}
                />

                <HeatMapDataCard
                  chartType="heatmap"
                  title="Offspeed - Outs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  filters={{ is_offspeed: "eq.true", is_out: "eq.true" }}
                />
              </>
            )}

            {/* Curveball heatmaps */}
            {dateFrom && dateTo && hasCurveball && (
              <>
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Curveball - Outs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Curveball"
                  filters={{ is_out: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Curveball - Hits"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Curveball"
                  filters={{ is_hit: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Curveball - Contact"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Curveball"
                  filters={{ is_contact: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Curveball - Whiffs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Curveball"
                  filters={{ is_whiff: "eq.true" }}
                />
              </>
            )}

            {/* Slider heatmaps */}
            {dateFrom && dateTo && hasSlider && (
              <>
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Slider - Outs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Slider"
                  filters={{ is_out: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Slider - Hits"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Slider"
                  filters={{ is_hit: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Slider - Contact"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Slider"
                  filters={{ is_contact: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Slider - Whiffs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Slider"
                  filters={{ is_whiff: "eq.true" }}
                />
              </>
            )}

            {/* Cutter heatmaps */}
            {dateFrom && dateTo && hasCutter && (
              <>
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Cutter - Outs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Cutter"
                  filters={{ is_out: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Cutter - Hits"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Cutter"
                  filters={{ is_hit: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Cutter - Contact"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Cutter"
                  filters={{ is_contact: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Cutter - Whiffs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Cutter"
                  filters={{ is_whiff: "eq.true" }}
                />
              </>
            )}

            {/* Other heatmaps */}
            {dateFrom && dateTo && hasOther && (
              <>
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Other - Outs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Other"
                  filters={{ is_out: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Other - Hits"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Other"
                  filters={{ is_hit: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Other - Contact"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Other"
                  filters={{ is_contact: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Other - Whiffs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Other"
                  filters={{ is_whiff: "eq.true" }}
                />
              </>
            )}

            {/* Changeup heatmaps */}
            {dateFrom && dateTo && hasChangeup && (
              <>
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Changeup - Outs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Changeup"
                  filters={{ is_out: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Changeup - Hits"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Changeup"
                  filters={{ is_hit: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Changeup - Contact"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Changeup"
                  filters={{ is_contact: "eq.true" }}
                />
                <HeatMapDataCard
                  chartType="heatmap"
                  title="Changeup - Whiffs"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="pitcher"
                  vsHandedness={vsHand}
                  pitchType="Changeup"
                  filters={{ is_whiff: "eq.true" }}
                />
              </>
            )}

            <HeatMapDataCard
              chartType="avg_exit_velocity_by_zone"
              title="Average Exit Velocity by Zone"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="pitcher"
              vsHandedness={vsHand}
            />

            <HeatMapDataCard
              chartType="contact_rate_by_zone"
              title="Contact Rate by Zone"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="pitcher"
              vsHandedness={vsHand}
            />

            <HeatMapDataCard
              chartType="whiff_rate_by_zone"
              title="Whiff Rate by Zone"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="pitcher"
              vsHandedness={vsHand}
            />

            <HeatMapDataCard
              chartType="hit_rate_by_zone"
              title="Hit Rate by Zone"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="pitcher"
              vsHandedness={vsHand}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 grid-rows-1 gap-2 w-full">
            <SprayChartDataCard
              title="Hits"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="pitcher"
              vsHandedness={vsHand}
              filters={{ is_hit: "eq.true" }}
            />
            <SprayChartDataCard
              title="Outs"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="pitcher"
              vsHandedness={vsHand}
              filters={{ is_out: "eq.true" }}
            />
            <SprayChartDataCard
              title="All Balls in Play"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="pitcher"
              vsHandedness={vsHand}
            />
          </div>
        </>
      )}
    </div>
  );
};
